import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FOOT_POINTS_COUNT } from 'context/FootMeshContext';
import { findSoleDirection } from 'Utils/FootMeshUtils';

export const FOOT_POINT_COLORS = ['#e53935', '#43a047', '#1e88e5'];

interface FootPointPickerSceneProps {
  geometry: THREE.BufferGeometry | null;
  points: THREE.Vector3[];
  onPointsChange: (points: THREE.Vector3[]) => void;
  isDarkMode: boolean;
}

const createControls = (camera: THREE.Camera, domElement: HTMLElement) => {
  const controls = new OrbitControls(camera, domElement);
  controls.enableDamping = true;
  return controls;
};

// Max pointer travel (px) between down and up that still counts as a click rather than an orbit drag.
const CLICK_TOLERANCE_PX = 4;

const FootPointPickerScene: React.FC<FootPointPickerSceneProps> = ({ geometry, points, onPointsChange, isDarkMode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const controlsRef = useRef<OrbitControls>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const meshRef = useRef<THREE.Mesh | null>(null);
  const markersRef = useRef<THREE.Group>(new THREE.Group());
  const pointsRef = useRef(points);
  const onPointsChangeRef = useRef(onPointsChange);
  pointsRef.current = points;
  onPointsChangeRef.current = onPointsChange;

  // Scene setup
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / Math.max(container.clientHeight, 1), 0.1, 10000);
    camera.position.set(0, -300, 300);
    camera.up.set(0, 0, 1);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    controlsRef.current = createControls(camera, renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const keyLight = new THREE.DirectionalLight(0xffffff, 2);
    keyLight.position.set(1, -1, 2);
    camera.add(keyLight);
    scene.add(camera);
    scene.add(markersRef.current);

    let frameId = 0;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      controlsRef.current?.update();
      renderer.render(scene, camera);
    };
    animate();

    const resizeObserver = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = container;
      if (!clientWidth || !clientHeight) return;
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
    });
    resizeObserver.observe(container);

    // Picking: a click (not a drag) on the mesh adds a point while fewer than FOOT_POINTS_COUNT exist.
    const raycaster = new THREE.Raycaster();
    let downPos: { x: number; y: number } | null = null;
    const onPointerDown = (e: PointerEvent) => {
      downPos = e.button === 0 ? { x: e.clientX, y: e.clientY } : null;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (!downPos || !meshRef.current) return;
      const moved = Math.hypot(e.clientX - downPos.x, e.clientY - downPos.y);
      downPos = null;
      if (moved > CLICK_TOLERANCE_PX || pointsRef.current.length >= FOOT_POINTS_COUNT) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.setFromCamera(ndc, camera);
      const hit = raycaster.intersectObject(meshRef.current, false)[0];
      if (!hit) return;
      const localPoint = meshRef.current.worldToLocal(hit.point.clone());
      onPointsChangeRef.current([...pointsRef.current, localPoint]);
    };
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      controlsRef.current?.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  useEffect(() => {
    sceneRef.current!.background = new THREE.Color(isDarkMode ? 0x2a2a2a : 0xf1f0f0);
  }, [isDarkMode]);

  // Mesh update: center the model in the scene and frame the camera on it.
  useEffect(() => {
    const scene = sceneRef.current!;
    if (meshRef.current) {
      scene.remove(meshRef.current);
      (meshRef.current.material as THREE.Material).dispose();
      meshRef.current = null;
    }
    if (!geometry) return;

    const mesh = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({ color: 0xd8b49c, roughness: 0.7, metalness: 0.05, side: THREE.DoubleSide })
    );
    geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    const center = box.getCenter(new THREE.Vector3());
    mesh.position.copy(center).negate();
    scene.add(mesh);
    meshRef.current = mesh;

    // Start from the bottom view so the sole points are easy to pick.
    const size = box.getSize(new THREE.Vector3()).length() || 1;
    const camera = cameraRef.current!;
    const { sole, length } = findSoleDirection(geometry);
    camera.near = size / 1000;
    camera.far = size * 100;
    camera.up.copy(length);
    camera.position.copy(sole).multiplyScalar(size * 1.2);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    // OrbitControls captures camera.up when created, so rebuild them for the new up direction.
    controlsRef.current?.dispose();
    controlsRef.current = createControls(camera, rendererRef.current!.domElement);
  }, [geometry]);

  // Markers update
  useEffect(() => {
    const markers = markersRef.current;
    markers.children.forEach((child) => {
      const marker = child as THREE.Mesh;
      marker.geometry.dispose();
      (marker.material as THREE.Material).dispose();
    });
    markers.clear();
    const mesh = meshRef.current;
    if (!mesh || !geometry?.boundingBox) return;

    const radius = geometry.boundingBox.getSize(new THREE.Vector3()).length() * 0.012;
    points.forEach((point, index) => {
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(radius, 16, 12),
        new THREE.MeshBasicMaterial({ color: FOOT_POINT_COLORS[index % FOOT_POINT_COLORS.length], depthTest: false })
      );
      marker.renderOrder = 1;
      marker.position.copy(point).add(mesh.position);
      markers.add(marker);
    });
  }, [points, geometry]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', cursor: 'crosshair' }} />;
};

export default FootPointPickerScene;
